import Editor from "@monaco-editor/react";
import { Braces, Wand2 } from "lucide-react";
import { useEffect, useState } from "react";

import { parseRawJsonVariables } from "./raw-json-import-utils";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { ToastType } from "@/enums";
import { cn } from "@/lib/utils";
import { stringifyVariables } from "@/utils/playground";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { formatJson } from "@/utils/utils";

interface RawJsonImportProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onVariablesFetched?: (variables: Record<string, string>, setName?: string) => void;
}

export const RawJsonImport = ({ isOpen, onOpenChange, onVariablesFetched }: RawJsonImportProps) => {
  const [jsonText, setJsonText] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setJsonText("");
      setError(null);
    }
  }, [isOpen]);

  const handleFormat = () => {
    if (!jsonText.trim()) {
      setError("Please paste a JSON object");
      return;
    }

    try {
      JSON.parse(jsonText);
      setJsonText(formatJson(jsonText));
      setError(null);
    } catch {
      setError("Invalid JSON. Fix the syntax before formatting.");
    }
  };

  const handleImport = () => {
    const result = parseRawJsonVariables(jsonText);
    if (!result.ok) {
      setError(result.error);
      displayErrorToast(result.error);
      return;
    }

    const variables = stringifyVariables(
      result.variables as Record<string, string | boolean | number | object>,
    );

    onVariablesFetched?.(variables);
    displayToast("Variables imported successfully!", ToastType.success);
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Braces className="h-5 w-5 text-gray-500" />
            Import from Raw JSON
          </DialogTitle>
          <DialogDescription>
            Paste a JSON object. Each top-level key becomes an input variable.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              Example: <span className="font-mono">{`{ "user_name": "Alice" }`}</span>
            </p>
            <BasicTooltip content="Format with AI">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleFormat}
                disabled={!jsonText.trim()}
                aria-label="Format with AI"
              >
                <Wand2 className="h-4 w-4" />
              </Button>
            </BasicTooltip>
          </div>

          <div
            className={cn(
              "min-h-[300px] overflow-hidden rounded-lg border border-border/40",
              error && "border-red-500",
            )}
          >
            <Editor
              height="300px"
              language="json"
              value={jsonText}
              onChange={value => {
                setJsonText(value ?? "");
                if (error) {
                  setError(null);
                }
              }}
              options={{
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 13,
                lineHeight: 20,
                padding: { top: 12, bottom: 12 },
                renderLineHighlight: "none",
                fontFamily: "menlo, monaco, monospace",
                automaticLayout: true,
                wordWrap: "on",
                tabSize: 2,
                folding: true,
                glyphMargin: false,
                lineDecorationsWidth: 8,
                lineNumbersMinChars: 3,
              }}
            />
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={!jsonText.trim()}>
            Import Variables
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
